'use client';

// Viral-share session guard (MOR-540). The "I just Nx'd this" celebration
// auto-opens at most ONCE per market per session, so a user who re-sells the
// same winning market isn't nagged with the same modal again. State is
// in-memory only — a page reload resets it, which is the intended cadence.

import type { StoreApi, UseBoundStore } from 'zustand';
import { createTrackedStore } from '@/lib/dev/zustand-registry';

interface ViralShareState {
  /** Market pubkeys whose viral celebration has already fired this session. */
  celebratedMarkets: ReadonlySet<string>;
  /** True if this market has already auto-celebrated this session. */
  hasCelebrated: (marketPubkey: string) => boolean;
  /** Record that this market's celebration fired. Idempotent. */
  markCelebrated: (marketPubkey: string) => void;
}

export const useViralShareStore: UseBoundStore<StoreApi<ViralShareState>> =
  createTrackedStore<ViralShareState>('useViralShareStore')((set, get) => ({
    celebratedMarkets: new Set<string>(),
    hasCelebrated(marketPubkey: string): boolean {
      return get().celebratedMarkets.has(marketPubkey);
    },
    markCelebrated(marketPubkey: string): void {
      if (marketPubkey.length === 0) return;
      set((state) => {
        if (state.celebratedMarkets.has(marketPubkey)) return state;
        const next = new Set(state.celebratedMarkets);
        next.add(marketPubkey);
        return { celebratedMarkets: next };
      });
    },
  }));
